/**
 * Editor de publicaciones del blog.
 *
 * Reemplaza el `<textarea>` anterior. Lo que cambia en el modelo de datos:
 *
 * - `content` pasa de `{ body: string[] }` a `{ version, doc, body }`. El
 *   `body` se sigue escribiendo porque hay lecturas legacy; el `doc` es lo que
 *   el editor y el renderer usan.
 * - El guardado escribe los campos derivados (`toc`, `faq`) que el sitio
 *   consume, para no recalcularlos en cada visita.
 * - Se separatesAuthor del CMS (`author_id`) del autor público
 *   (`public_author_id`), porque el byline no puede salir de `profiles`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Input } from "@vimos/ui";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../lib/auth";
import { notifyCmsChanged } from "../../lib/cms-webhook";
import type { BlogAuthor, BlogCategory, BlogPost } from "@vimos/api-client";
import {
  EMPTY_DOC,
  buildExcerpt,
  normalizeRichContent,
  readingStats,
  renderRichContent,
  splitFaq,
  toRichContent,
  withFaqEntries,
} from "@vimos/rich-content";
import type { FaqEntry, RichDoc } from "@vimos/rich-content";
import { MediaPicker } from "../media/MediaPicker";
import { RichTextEditor } from "./editor/RichTextEditor";
import { ArticlePreview, SeoPanel } from "./editor/SeoPanels";

interface FormValues {
  title: string;
  slug: string;
  excerpt: string;
  cover_image_url: string;
  cover_image_alt: string;
  category_id: string;
  seo_title: string;
  seo_description: string;
  focus_keyword: string;
  secondary_keywords: string;
  public_author_id: string;
  related_slugs: string;
  cta_title: string;
  cta_description: string;
  cta_label: string;
  cta_url: string;
  review_status: "draft" | "in_review" | "scheduled" | "approved";
  is_published: boolean;
}

async function fetchPost(id: string): Promise<BlogPost> {
  const { data, error } = await supabase.from("blog_posts").select("*").eq("id", id).single();
  if (error) throw error;
  return data;
}

async function fetchCategories(): Promise<BlogCategory[]> {
  const { data, error } = await supabase
    .from("blog_categories")
    .select("*")
    .order("name", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

async function fetchAuthors(): Promise<BlogAuthor[]> {
  const { data, error } = await supabase
    .from("blog_authors")
    .select("*")
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/** Slugs de otros artículos, para elegir relacionados sin incluirse a sí mismo. */
async function fetchOtherPosts(currentId: string | null): Promise<{ slug: string; title: string }[]> {
  let query = supabase.from("blog_posts").select("slug, title").eq("is_published", true).order("title");
  if (currentId) query = query.neq("id", currentId);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

export function BlogEditorPage() {
  const { id: recordId } = useParams<{ id: string }>();
  const isNew = recordId === "nuevo";
  const postId = isNew ? null : recordId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile, session } = useAuth();

  const hasValidId = isNew || Boolean(postId);

  // El documento vive fuera de react-hook-form: es un objeto grande que se
  // reemplaza entero en cada cambio, y meterlo en el form haría que RHF
  // comparara el árbol completo en cada tecla.
  const [doc, setDoc] = useState<RichDoc>(EMPTY_DOC);
  const [faq, setFaq] = useState<FaqEntry[]>([]);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">("desktop");
  const slugTouched = useRef(false);

  const { data: categories } = useQuery({ queryKey: ["blog_categories"], queryFn: fetchCategories });
  const { data: authors } = useQuery({ queryKey: ["blog_authors"], queryFn: fetchAuthors });
  const { data: otherPosts } = useQuery({
    queryKey: ["blog_posts", "related-options", postId],
    queryFn: () => fetchOtherPosts(postId ?? null),
  });

  const { data: existingPost, isLoading } = useQuery({
    queryKey: ["blog_posts", postId],
    queryFn: () => fetchPost(postId!),
    enabled: !isNew && Boolean(postId),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      title: "",
      slug: "",
      excerpt: "",
      cover_image_url: "",
      cover_image_alt: "",
      category_id: "",
      seo_title: "",
      seo_description: "",
      focus_keyword: "",
      secondary_keywords: "",
      public_author_id: "",
      related_slugs: "",
      cta_title: "",
      cta_description: "",
      cta_label: "",
      cta_url: "",
      review_status: "draft",
      is_published: false,
    },
  });

  const watched = useWatch({ control });
  const coverImageUrl = useWatch({ control, name: "cover_image_url" });

  // Se registra una sola vez y se reutiliza: el Input necesita interceptar el
  // onChange de RHF para marcar el slug como editado a mano.
  const slugField = register("slug", {
    required: "El slug es obligatorio",
    pattern: { value: /^[a-z0-9-]+$/, message: "Solo minusculas, numeros y guiones" },
  });

  useEffect(() => {
    if (!existingPost) return;
    // El FAQ viaja dentro del documento pero se edita aparte: se separa al
    // cargar y se reinserta al guardar (`withFaqEntries`).
    const { doc: body, faq: loadedFaq } = splitFaq(normalizeRichContent(existingPost.content));

    setDoc(body);
    setFaq(loadedFaq);
    slugTouched.current = true;
    reset({
      title: existingPost.title,
      slug: existingPost.slug,
      excerpt: existingPost.excerpt,
      cover_image_url: existingPost.cover_image_url ?? "",
      cover_image_alt: existingPost.cover_image_alt ?? "",
      category_id: existingPost.category_id ?? "",
      seo_title: existingPost.seo_title ?? "",
      seo_description: existingPost.seo_description ?? "",
      focus_keyword: existingPost.focus_keyword ?? "",
      secondary_keywords: Array.isArray(existingPost.secondary_keywords)
        ? existingPost.secondary_keywords.join(", ")
        : "",
      public_author_id: existingPost.public_author_id ?? "",
      related_slugs: Array.isArray(existingPost.related_post_ids)
        ? existingPost.related_post_ids.join(", ")
        : "",
      cta_title: existingPost.cta_title ?? "",
      cta_description: existingPost.cta_description ?? "",
      cta_label: existingPost.cta_label ?? "",
      cta_url: existingPost.cta_url ?? "",
      review_status: existingPost.review_status ?? "draft",
      is_published: existingPost.is_published,
    });
  }, [existingPost, reset]);

  // El slug sigue al título solo mientras el autor no lo haya tocado a mano:
  // en un artículo existente nunca se sobrescribe lo que ya está publicado.
  useEffect(() => {
    if (!slugTouched.current && isNew) {
      const title = getValues("title");
      if (title) setValue("slug", slugify(title), { shouldDirty: false });
    }
  }, [watched.title, isNew, getValues, setValue]);

  const handleContentChange = useCallback((nextDoc: RichDoc, nextFaq: FaqEntry[]) => {
    setDoc(nextDoc);
    setFaq(nextFaq);
  }, []);

  const stats = useMemo(() => readingStats(doc), [doc]);
  // El preview renderiza el mismo doc que se guardará, FAQ incluido: si el
  // preview y la página pública difirieran, el preview no serviría de nada.
  const composedDoc = useMemo(() => withFaqEntries(doc, faq), [doc, faq]);
  const authorName = useMemo(
    () => authors?.find((author) => author.id === watched.public_author_id)?.name ?? "Equipo Vimos Digital",
    [authors, watched.public_author_id],
  );

  const relatedPreview = useMemo(() => {
    const slugs = (watched.related_slugs ?? "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 3);
    return slugs
      .map((slug) => otherPosts?.find((item) => item.slug === slug))
      .filter((item): item is { slug: string; title: string } => Boolean(item))
      .map((item) => ({ title: item.title, category: "Blog", href: item.slug }));
  }, [watched.related_slugs, otherPosts]);

  const mutation = useMutation<string, Error, FormValues>({
    mutationFn: async (values: FormValues) => {
      // El FAQ vuelve al documento antes de renderizar: `toc` y `faq` se
      // calculan sobre lo que realmente se va a guardar, no sobre el cuerpo solo.
      const composed = withFaqEntries(doc, faq);
      const rendered = renderRichContent(composed, { wrapper: null });
      const excerpt = values.excerpt.trim() || buildExcerpt(composed);
      if (!excerpt) {
        throw new Error("Agrega un extracto: las tarjetas del listado lo necesitan.");
      }

      const payload = {
        title: values.title.trim(),
        slug: values.slug.trim(),
        excerpt,
        cover_image_url: values.cover_image_url || null,
        cover_image_alt: values.cover_image_alt || null,
        category_id: values.category_id || null,
        // Se guarda el doc estructurado y, en la misma fila, `body` para que
        // cualquier lectura legacy siga funcionando sin migración.
        content: toRichContent(composed) as unknown as Record<string, unknown>,
        seo_title: values.seo_title || null,
        seo_description: values.seo_description || null,
        focus_keyword: values.focus_keyword || null,
        secondary_keywords: values.secondary_keywords
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        toc: rendered.toc as unknown as unknown[],
        faq: rendered.faq as unknown as unknown[],
        related_post_ids: values.related_slugs
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .slice(0, 3),
        cta_title: values.cta_title || null,
        cta_description: values.cta_description || null,
        cta_label: values.cta_label || null,
        cta_url: values.cta_url || null,
        public_author_id: values.public_author_id || null,
        review_status: values.review_status,
        is_published: values.is_published,
        published_at: values.is_published ? (existingPost?.published_at ?? new Date().toISOString()) : null,
        author_id: existingPost?.author_id ?? profile?.id ?? null,
      };

      // La interfaz de escritura de postgrest-js v2 no resuelve contra el
      // `Database` manual (mismo trabajo-around que el resto del admin).
      if (isNew) {
        const { data, error } = await supabase
          .from("blog_posts")
          .insert(payload as never)
          .select("id")
          .single();
        if (error) throw error;
        const insertedId = (data as { id: string } | null)?.id;
        if (!insertedId) throw new Error("No se pudo obtener el ID de la publicacion creada.");
        return insertedId;
      }

      if (!postId) throw new Error("ID de publicacion requerido.");
      const { error } = await supabase
        .from("blog_posts")
        .update(payload as never)
        .eq("id", postId);
      if (error) throw error;
      return postId;
    },
    onSuccess: async (savedId: string) => {
      queryClient.invalidateQueries({ queryKey: ["blog_posts"] });
      queryClient.invalidateQueries({ queryKey: ["blog_authors"] });

      try {
        await notifyCmsChanged("blog_posts", isNew ? "insert" : "update", savedId, session?.access_token);
      } catch (webhookError) {
        console.error("Error al notificar cambio CMS", webhookError);
      }

      navigate("/blog");
    },
  });

  return (
    <div className="max-w-7xl">
      {!hasValidId ? (
        <p className="mt-6 text-red-600">ID de publicacion invalido.</p>
      ) : (
        <>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-signal-hover font-mono text-xs uppercase">Blog</p>
              <h1 className="font-display text-ink mt-2 text-3xl font-bold">
                {isNew ? "Nueva publicacion" : "Editar publicacion"}
              </h1>
              <p className="text-mist mt-1 text-sm">
                Titulo, imagen, categoria, autor, SEO, relacionados y cierre comercial. El indice y el FAQ se
                derivan del contenido.
              </p>
            </div>
            <Button type="button" variant="ghost" onClick={() => navigate("/blog")}>
              Volver
            </Button>
          </div>

          {isLoading ? <p className="text-mist mt-6">Cargando...</p> : null}

          <form
            onSubmit={handleSubmit((values) => mutation.mutate(values))}
            className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]"
          >
            <div className="space-y-5">
              <section className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
                <h2 className="font-display text-ink text-xl font-bold">Informacion editorial</h2>
                <p className="text-mist mt-1 text-sm">
                  El titulo es el H1 de la pagina. Dentro del contenido solo se usan H2 y H3.
                </p>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Titulo"
                    error={errors.title?.message}
                    {...register("title", { required: "El titulo es obligatorio" })}
                  />
                  <Input
                    label="Slug"
                    hint="URL publica: /blog/tu-slug"
                    error={errors.slug?.message}
                    {...slugField}
                    onChange={(event) => {
                      // Cualquier edición manual corta el autocompletado: a
                      // partir de aquí el slug es del autor, no del título.
                      slugTouched.current = true;
                      slugField.onChange(event);
                    }}
                  />
                  <div className="sm:col-span-2">
                    <Input
                      label="Extracto"
                      hint="Texto de la tarjeta en los listados"
                      error={errors.excerpt?.message}
                      {...register("excerpt")}
                    />
                  </div>
                  <MediaPicker
                    label="Imagen principal"
                    value={coverImageUrl ?? ""}
                    onChange={(url) => {
                      if (typeof url === "string") {
                        setValue("cover_image_url", url, { shouldDirty: true });
                      }
                    }}
                  />
                  <Input
                    label="ALT de la imagen principal"
                    hint="Describe la imagen. Lo leen los lectores de pantalla y los buscadores."
                    {...register("cover_image_alt")}
                  />
                  <input
                    type="hidden"
                    {...register("cover_image_url", {
                      validate: (value) => {
                        if (!value) return true;
                        try {
                          const url = new URL(value, window.location.origin);
                          return (
                            ["http:", "https:"].includes(url.protocol) || "Usa una URL http o https valida"
                          );
                        } catch {
                          return "Usa una URL http o https valida";
                        }
                      },
                    })}
                  />
                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-ink text-sm font-medium">Categoria</label>
                    <select
                      className="border-sand/70 bg-paper text-ink h-11 rounded-md border px-3.5 text-sm"
                      {...register("category_id")}
                    >
                      <option value="">Sin categoria</option>
                      {categories?.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-ink text-sm font-medium">Autor visible</label>
                    <select
                      className="border-sand/70 bg-paper text-ink h-11 rounded-md border px-3.5 text-sm"
                      {...register("public_author_id")}
                    >
                      <option value="">Equipo Vimos Digital (por defecto)</option>
                      {authors?.map((author) => (
                        <option key={author.id} value={author.id}>
                          {author.name}
                          {author.is_public ? "" : " (oculto en la web)"}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              <section className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-display text-ink text-xl font-bold">Contenido</h2>
                  <span className="text-mist font-mono text-xs">
                    {stats.words} palabras · ~{stats.minutes} min de lectura
                  </span>
                </div>
                <p className="text-mist mt-1 text-sm">
                  Pega desde Word, Google Docs o ChatGPT: se conserva la estructura y se descartan estilos que
                  no pertenecen al artículo.
                </p>

                <RichTextEditor doc={doc} faq={faq} onChange={handleContentChange} />
              </section>

              <section className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
                <h2 className="font-display text-ink text-xl font-bold">SEO</h2>
                <p className="text-mist mt-1 text-sm">
                  Si lo dejas vacio, el sitio usa el titulo y el extracto.
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <Input label="Titulo SEO" {...register("seo_title")} />
                  <Input label="Descripcion SEO" {...register("seo_description")} />
                  <Input
                    label="Palabra clave principal"
                    hint="Se revisa su presencia en titulo, introduccion, encabezados y slug"
                    {...register("focus_keyword")}
                  />
                  <Input
                    label="Palabras clave secundarias"
                    hint="Separadas por coma"
                    {...register("secondary_keywords")}
                  />
                </div>
              </section>

              <section className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
                <h2 className="font-display text-ink text-xl font-bold">Cierre comercial</h2>
                <p className="text-mist mt-1 text-sm">
                  Aparece al final del artículo. Necesita botón y URL: un título solo no puede convertir.
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <Input label="Titulo del CTA" {...register("cta_title")} />
                  <Input label="Texto del botón" {...register("cta_label")} />
                  <div className="sm:col-span-2">
                    <Input label="Descripción" {...register("cta_description")} />
                  </div>
                  <Input
                    label="URL del botón"
                    hint="Ruta interna o URL completa"
                    {...register("cta_url", {
                      validate: (value) => {
                        if (!value) return true;
                        if (/^https?:\/\//i.test(value) || (value.startsWith("/") && !value.startsWith("//")))
                          return true;
                        return "Usa una ruta interna (/contacto) o una URL http/https";
                      },
                    })}
                  />
                </div>
              </section>

              <section className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
                <h2 className="font-display text-ink text-xl font-bold">Articulos relacionados</h2>
                <p className="text-mist mt-1 text-sm">
                  Elige hasta 3. Si no alcanzas, el sitio completa con articulos de la misma categoria.
                </p>

                <div className="mt-4 space-y-2">
                  {(otherPosts ?? []).slice(0, 12).map((option) => {
                    const selected = (watched.related_slugs ?? "")
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean);
                    const isSelected = selected.includes(option.slug);
                    const full = selected.length >= 3 && !isSelected;

                    return (
                      <button
                        key={option.slug}
                        type="button"
                        disabled={full}
                        onClick={() => {
                          const next = isSelected
                            ? selected.filter((slug) => slug !== option.slug)
                            : [...selected, option.slug].slice(0, 3);
                          setValue("related_slugs", next.join(", "), { shouldDirty: true });
                        }}
                        className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:opacity-40 ${
                          isSelected
                            ? "border-navy bg-navy text-white"
                            : "border-sand/60 text-ink hover:bg-sand/30"
                        }`}
                      >
                        <span className="truncate">{option.title}</span>
                        <span className="font-mono text-[10px] opacity-70">{option.slug}</span>
                      </button>
                    );
                  })}
                </div>
                <input type="hidden" {...register("related_slugs")} />
              </section>

              <section className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
                <h2 className="font-display text-ink text-xl font-bold">Estado</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label className="font-body text-ink text-sm font-medium">Estado editorial</label>
                    <select
                      className="border-sand/70 bg-paper text-ink h-11 rounded-md border px-3.5 text-sm"
                      {...register("review_status")}
                    >
                      <option value="draft">Borrador</option>
                      <option value="in_review">En revision</option>
                      <option value="scheduled">Programado</option>
                      <option value="approved">Aprobado</option>
                    </select>
                  </div>
                  <label className="text-ink mt-7 flex items-center gap-2 text-sm">
                    <input type="checkbox" className="h-4 w-4" {...register("is_published")} />
                    Visible en el sitio publico
                  </label>
                </div>
                <p className="text-mist mt-2 text-xs">
                  La casilla es la unica fuente de verdad de publicacion. El estado editorial es informacion
                  de trabajo.
                </p>
              </section>

              {mutation.isError && (
                <p
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {mutation.error instanceof Error
                    ? mutation.error.message
                    : "No pudimos guardar la publicacion."}
                </p>
              )}

              <div className="border-sand/40 sticky bottom-0 flex flex-wrap gap-3 border-t bg-[#f7f7f4]/95 py-4 backdrop-blur">
                <Button type="submit" isLoading={isSubmitting || mutation.isPending}>
                  Guardar cambios
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setValue("is_published", false);
                    mutation.mutate({ ...getValues(), is_published: false });
                  }}
                >
                  Guardar borrador
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setValue("is_published", true);
                    mutation.mutate({ ...getValues(), is_published: true });
                  }}
                >
                  Publicar
                </Button>
                <Button type="button" variant="ghost" onClick={() => navigate("/blog")}>
                  Cancelar
                </Button>
              </div>
            </div>

            <aside className="space-y-5 xl:sticky xl:top-6 xl:h-max">
              <ArticlePreview
                doc={composedDoc}
                title={watched.title ?? ""}
                excerpt={watched.excerpt ?? ""}
                category={categories?.find((item) => item.id === watched.category_id)?.name ?? "Categoria"}
                author={authorName}
                date={existingPost?.published_at ?? new Date().toISOString()}
                coverImageUrl={watched.cover_image_url ?? ""}
                coverImageAlt={watched.cover_image_alt ?? ""}
                readingMinutes={stats.minutes}
                faqCount={faq.length}
                cta={
                  watched.cta_label && watched.cta_url
                    ? {
                        title: watched.cta_title || "¿Hablamos de tu proyecto?",
                        description: watched.cta_description ?? "",
                        label: watched.cta_label,
                        url: watched.cta_url,
                      }
                    : null
                }
                related={relatedPreview}
                device={previewDevice}
                onDeviceChange={setPreviewDevice}
              />

              <SeoPanel
                doc={composedDoc}
                title={watched.title ?? ""}
                excerpt={watched.excerpt ?? ""}
                slug={watched.slug ?? ""}
                seoTitle={watched.seo_title ?? ""}
                seoDescription={watched.seo_description ?? ""}
                coverImageUrl={watched.cover_image_url ?? ""}
                coverImageAlt={watched.cover_image_alt ?? ""}
                focusKeyword={watched.focus_keyword ?? ""}
                secondaryKeywords={watched.secondary_keywords ?? ""}
              />

              <div className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
                <Badge tone={watched.is_published ? "market" : "neutral"}>
                  {watched.is_published ? "Publicado" : "Borrador"}
                </Badge>
                <h2 className="font-display text-ink mt-4 text-2xl font-bold">
                  {watched.title || "Titulo del articulo"}
                </h2>
                <p className="text-mist mt-3 text-sm leading-relaxed">
                  {watched.excerpt || "Extracto visible en el listado publico."}
                </p>
                <p className="text-mist mt-4 rounded-md bg-[#f7f7f4] px-3 py-2 font-mono text-xs">
                  /blog/{watched.slug || "slug-del-articulo"}
                </p>
              </div>
            </aside>
          </form>
        </>
      )}
    </div>
  );
}
